import type { RefinementCtx, ZodTypeAny } from 'zod'
import type { FormFieldType } from '../../../const/formField'

import type { BaseInputUploaderRemotePayload } from '../../../ui/input-uploader/types'
import { isFuture, parseISO } from 'date-fns'

import { any, array, boolean, date, number, object, string, z, enum as zodEnum, ZodIssueCode } from 'zod'
import { ERRORS } from '../../../const/errors'
import { MediaType } from '../../../const/files'
import { FormField } from '../../../const/formField'

import {
  REGEX_FOR_CHECK_EMAIL,
  REGEX_FOR_CHECK_PHONE,
  REGEX_FOR_CYRILLIC,
  REGEX_FOR_PASSWORD,
  REGEX_OF_DECIMAL_PHONE,
} from '../../../const/regex'
import { isBaseInputUploaderRemotePayload } from '../../../ui/input-uploader/types'

const fileUploaderEntryLocalOrRemote = z.union([
  z.instanceof(File),
  z.custom<BaseInputUploaderRemotePayload>(
    (value): value is BaseInputUploaderRemotePayload =>
      isBaseInputUploaderRemotePayload(value),
    { message: ERRORS.INVALID_MEDIA_TYPE },
  ),
])

export const pureValidator = {
  typography: string(),
  string: string(),
  textarea: string(),
  fio: string()
    .optional()
    .refine(
      (value) => {
        if (!value) {
          return true
        }
        return value.length >= 2 && value.length <= 100
      },
      { message: ERRORS.INVALID_FIO },
    )
    .refine(
      (value) => {
        if (!value) {
          return true
        }
        return REGEX_FOR_CYRILLIC.test(value)
      },
      { message: ERRORS.INVALID_FIO },
    ),
  nickname: string().refine(value => /^[\w\s\-]+$/.test(value), {
    message: ERRORS.INVALID_NICKNAME,
  }),
  number: number().nullable(),
  phone: string(),
  email: string().refine((value) => {
    if (!value) {
      return true
    }
    return REGEX_FOR_CHECK_EMAIL.test(value)
  }, {
    message: ERRORS.INVALID_EMAIL,
  }),
  phoneOrEmail: string(),
  password: string(),
  repeatPassword: string(),
  checkbox: boolean(),
  checkboxGroup: array(string()),
  radioGroup: string(),
  radio: string(),
  code: string(),
  date: date().nullable().or(string().nullable()),
  time: string().refine(
    (value) => {
      if (!value || value.length === 0) {
        return true
      }
      return /^(?:[01]?\d|2[0-3]):[0-5]\d$/.test(value)
    },
    { message: ERRORS.INVALID_TIME },
  ),
  birthday: string().nullable(),
  select: string(),
  selectTags: array(string()),
  autocomplete: string(),
  button: string(),
  address: string().nullable(),
  inputMap: string().nullable(),
  divider: any(),
  fileUploader: z.union([
    array(object({
      src: string().nullable(),
      mediaId: string().nullable(),
      mediaType: zodEnum(Object.values(MediaType) as [string, ...string[]], {
        invalid_type_error: ERRORS.INVALID_MEDIA_TYPE,
      }),
    })),
    array(z.instanceof(File)),
    array(fileUploaderEntryLocalOrRemote),
  ]),
  colorPicker: string(),
  iconPicker: string(),
}

export const requiredValidator: Record<FormFieldType, ZodTypeAny> = {
  typography: pureValidator.typography,
  string: pureValidator.string.min(1, ERRORS.REQUIRED),
  textarea: pureValidator.textarea.min(1, ERRORS.REQUIRED),
  fio: string()
    .min(2, ERRORS.INVALID_FIO)
    .max(100, ERRORS.INVALID_FIO)
    .refine(value => /^[\p{Script=Cyrillic}\s\-]+$/u.test(value), {
      message: ERRORS.INVALID_FIO,
    }),
  nickname: pureValidator.nickname,
  number: number().min(1, ERRORS.REQUIRED),
  phone: pureValidator.phone
    .min(10, ERRORS.INVALID_MIN_PHONE_NUMBER)
    .regex(REGEX_OF_DECIMAL_PHONE, ERRORS.INVALID_PHONE),
  email: string()
    .email(ERRORS.INVALID_EMAIL)
    .min(1, ERRORS.REQUIRED),
  phoneOrEmail: pureValidator.phoneOrEmail
    .min(1, ERRORS.REQUIRED)
    .superRefine((val, ctx) => {
      const isPhone = Boolean(
        REGEX_FOR_CHECK_PHONE.test(val) || !Number.isNaN(+val),
      )

      if (isPhone && !REGEX_OF_DECIMAL_PHONE.test(val)) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message: ERRORS.INVALID_PHONE,
        })
        return
      }

      if (!isPhone && !REGEX_FOR_CHECK_EMAIL.test(val)) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message: ERRORS.INVALID_EMAIL,
        })
      }
    }),
  password: pureValidator.password
    .min(8, ERRORS.PASSWORD_MIN_LENGTH)
    .regex(REGEX_FOR_PASSWORD, ERRORS.PASSWORD_WEAK),
  repeatPassword: pureValidator.repeatPassword
    .min(8, ERRORS.PASSWORD_MIN_LENGTH)
    .regex(REGEX_FOR_PASSWORD, ERRORS.PASSWORD_WEAK),
  checkbox: pureValidator.checkbox.superRefine((val, ctx) => {
    if (!val) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: ERRORS.REQUIRED,
      })
    }
  }),
  checkboxGroup: pureValidator.checkboxGroup.min(1, ERRORS.REQUIRED),
  radioGroup: pureValidator.radioGroup.min(1, ERRORS.REQUIRED),
  radio: pureValidator.radio.superRefine((val, ctx) => {
    if (!val) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: ERRORS.REQUIRED,
      })
    }
  }),
  code: pureValidator.code.min(4, ERRORS.REQUIRED),
  time: string()
    .min(1, ERRORS.REQUIRED)
    .refine(
      value => /^(?:[01]?\d|2[0-3]):[0-5]\d$/.test(value),
      { message: ERRORS.INVALID_TIME },
    ),
  date: date({
    required_error: ERRORS.REQUIRED,
    invalid_type_error: ERRORS.INVALID_DATE,
  }).or(string().min(1, ERRORS.REQUIRED)),
  birthday: string()
    .min(1, ERRORS.REQUIRED)
    .superRefine((val, ctx) => {
      const date = parseISO(val)

      if (Number.isNaN(date.getTime())) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message: ERRORS.INVALID_DATE,
        })
        return
      }

      if (isFuture(date)) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message: ERRORS.FUTURE_DATE_NOT_ALLOWED,
        })
      }
    }),
  select: pureValidator.select.min(1, ERRORS.REQUIRED),
  selectTags: pureValidator.selectTags.min(1, ERRORS.REQUIRED),
  autocomplete: pureValidator.select.min(1, ERRORS.REQUIRED),
  button: pureValidator.button,
  address: string().min(1, ERRORS.REQUIRED),
  inputMap: string().min(1, ERRORS.REQUIRED),
  divider: any(),
  fileUploader: array(object({
    src: string().nullable(),
    mediaId: string().nullable(),
    mediaType: string().min(1, ERRORS.REQUIRED),
  })).superRefine((val, ctx) => {
    if (!val || val.length === 0) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: ERRORS.REQUIRED,
      })
    }
  }),
  colorPicker: pureValidator.colorPicker.min(1, ERRORS.REQUIRED),
  iconPicker: pureValidator.iconPicker.min(1, ERRORS.REQUIRED),
}

const validationConditions: Record<string, any> = {
  [FormField.repeatPassword](val: Record<string, any>, ctx: RefinementCtx) {
    const password = val[FormField.password]
    const repeatPassword = val[FormField.repeatPassword]
    if (password && repeatPassword !== password) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: ERRORS.PASSWORD_MISMATCH,
        path: [FormField.repeatPassword],
      })
    }
  },
}

export function validateObject(val: Record<string, any>, ctx: RefinementCtx) {
  Object.keys(val).forEach((valName) => {
    const func = validationConditions[valName as unknown as FormField]

    if (typeof func === 'function') {
      func(val, ctx)
    }
  })
}
